<template>
  <div class="page-layout">
    <div class="page-content">
      <h1>Редактирование события</h1>

      <div v-if="loading" class="panel">
        <p class="empty-state">Загрузка...</p>
      </div>

      <form v-else class="panel event-form" @submit.prevent="submit">
        <label>
          Название
          <input v-model="name" type="text" class="input" required />
        </label>

        <label>
          Обложка
          <input type="file" accept="image/jpeg,image/png,image/webp" @change="onCoverSelected" />
        </label>
        <div v-if="coverPreviewUrl || (existingCoverUrl && !coverRemoved)" class="event-cover-preview">
          <img
            :src="coverPreviewUrl || existingCoverUrl"
            alt="Обложка события"
            :class="{ 'event-cover-preview--removed': coverRemoved && !coverPreviewUrl }"
          />
          <button type="button" class="btn btn-secondary btn-sm" @click="removeCover">
            {{ coverRemoved ? 'Вернуть' : 'Удалить' }}
          </button>
        </div>

        <label>
          Описание
          <RichTextEditor
            v-model="description"
            :allow-images="false"
            placeholder="Расскажите о событии..."
          />
        </label>

        <label>
          Начало
          <input v-model="startsAt" type="datetime-local" class="input" required />
        </label>

        <label>
          Окончание
          <input v-model="endsAt" type="datetime-local" class="input" required />
        </label>

        <div class="form-field">
          <label for="edit-event-venue">Место проведения</label>
          <CityPicker
            :key="route.params.id"
            ref="venuePickerRef"
            v-model="venue"
            mode="place"
            input-id="edit-event-venue"
            placeholder="Начните вводить адрес или название"
          />
          <p class="field-hint">
            Начните вводить и выберите место из списка. Показывается на странице события, если треки не
            привязаны.
          </p>
        </div>

        <TrackMultiPicker v-model="trackIds" />

        <OrganizerPicker v-model="organizers" />

        <label class="checkbox-label">
          <input v-model="isPublic" type="checkbox" />
          Публичное событие
        </label>

        <label class="checkbox-label">
          <input v-model="allowComments" type="checkbox" />
          Разрешить комментарии
        </label>

        <p v-if="error" class="error-message">{{ error }}</p>

        <div class="btn-row">
          <button class="btn" type="submit" :disabled="submitting">
            {{ submitting ? 'Сохранение...' : 'Сохранить' }}
          </button>
          <router-link class="btn btn-secondary" :to="{ name: 'event', params: { id: route.params.id } }">
            Отмена
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import RichTextEditor from '../components/RichTextEditor.vue'
import CityPicker from '../components/CityPicker.vue'
import TrackMultiPicker from '../components/TrackMultiPicker.vue'
import OrganizerPicker from '../components/OrganizerPicker.vue'
import { fetchEvent, updateEvent } from '../api/events'
import { toDatetimeLocalValue } from '../utils/eventFormat'
import { organizersFromApi, organizersToApi } from '../utils/organizerDisplay'

const route = useRoute()
const router = useRouter()

const name = ref('')
const description = ref('')
const startsAt = ref('')
const endsAt = ref('')
const venue = ref(null)
const venuePickerRef = ref(null)
const trackIds = ref([])
const organizers = ref([])
const isPublic = ref(false)
const allowComments = ref(false)
const coverPhoto = ref(null)
const coverPreviewUrl = ref('')
const existingCoverUrl = ref('')
const coverRemoved = ref(false)
const loading = ref(true)
const submitting = ref(false)
const error = ref('')

let loadGeneration = 0

function onCoverSelected(event) {
  const file = event.target.files?.[0]
  if (!file) return

  if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value)
  coverPhoto.value = file
  coverPreviewUrl.value = URL.createObjectURL(file)
  coverRemoved.value = false
}

function removeCover() {
  if (coverPreviewUrl.value) {
    URL.revokeObjectURL(coverPreviewUrl.value)
    coverPreviewUrl.value = ''
    coverPhoto.value = null
    return
  }

  coverRemoved.value = !coverRemoved.value
}

async function loadEvent() {
  const generation = ++loadGeneration
  loading.value = true
  error.value = ''
  try {
    const { data } = await fetchEvent(route.params.id)
    if (generation !== loadGeneration) return

    name.value = data.name
    description.value = data.description || ''
    startsAt.value = toDatetimeLocalValue(data.starts_at)
    endsAt.value = toDatetimeLocalValue(data.ends_at)
    venue.value =
      data.venue && data.venue_lat != null && data.venue_lng != null
        ? { city: data.venue, lat: data.venue_lat, lng: data.venue_lng }
        : null
    trackIds.value = (data.tracks || []).map((track) => track.id)
    organizers.value = organizersFromApi(data.organizers, data.user_id)
    isPublic.value = data.public
    allowComments.value = data.allow_comments
    existingCoverUrl.value = data.cover_photo_url || ''
  } catch (err) {
    if (generation !== loadGeneration) return
    error.value = err.response?.data?.error || 'Не удалось загрузить событие'
  } finally {
    if (generation === loadGeneration) {
      loading.value = false
    }
  }
}

function venuePayload() {
  const selection = venuePickerRef.value?.getSelection?.()
  if (selection?.city) {
    return {
      venue: selection.city,
      venue_lat: selection.lat,
      venue_lng: selection.lng,
    }
  }

  return { venue: '', venue_lat: null, venue_lng: null }
}

async function submit() {
  if (venuePickerRef.value?.hasUnconfirmedInput?.()) {
    error.value = 'Выберите место проведения из списка подсказок'
    return
  }

  submitting.value = true
  error.value = ''
  try {
    const { data } = await updateEvent(
      route.params.id,
      {
        name: name.value,
        description: description.value,
        starts_at: new Date(startsAt.value).toISOString(),
        ends_at: new Date(endsAt.value).toISOString(),
        ...venuePayload(),
        track_ids: trackIds.value,
        organizers: organizersToApi(organizers.value),
        public: isPublic.value,
        allow_comments: allowComments.value,
      },
      {
        coverPhoto: coverPhoto.value,
        removeCoverPhoto: coverRemoved.value && !coverPhoto.value,
      }
    )
    router.push({ name: 'event', params: { id: data.id } })
  } catch (err) {
    const errors = err.response?.data?.errors
    error.value = errors?.join(', ') || err.response?.data?.error || 'Не удалось сохранить событие'
  } finally {
    submitting.value = false
  }
}

onMounted(loadEvent)
watch(() => route.params.id, loadEvent)

onBeforeUnmount(() => {
  if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value)
})
</script>

<style scoped>
.event-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.event-form label {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-weight: 600;
}

.event-form .input,
.event-form input[type='text'],
.event-form input[type='datetime-local'] {
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
}

.checkbox-label {
  flex-direction: row !important;
  align-items: center;
  font-weight: 500 !important;
}

.event-cover-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.event-cover-preview img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 0.5rem;
  object-fit: cover;
}

.event-cover-preview--removed {
  opacity: 0.5;
}

.field-hint {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  color: #64748b;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
</style>
